import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageSquare, Trash2, Plus } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const ChatSessions = ({ sessionId, onSelect, onNew, refreshKey }) => {
  const [sessions, setSessions] = useState([]);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/chat/sessions").then(({ data }) => setSessions(data.sessions));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const confirmDelete = async () => {
    await api.delete(`/chat/sessions/${toDelete.session_id}`);
    setToDelete(null);
    load();
    if (toDelete.session_id === sessionId) onNew();
  };

  return (
    <div className="w-56 shrink-0 hidden lg:flex flex-col bg-card border border-border rounded-lg overflow-hidden" data-testid="chat-sessions-panel">
      <div className="px-3 py-3 border-b border-border flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">History</span>
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onNew} data-testid="sessions-new-chat">
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sessions.length === 0 && <p className="text-xs text-muted-foreground p-2">No conversations yet.</p>}
        {sessions.map((s) => (
          <div key={s.session_id}
            className={`group flex items-start gap-2 rounded-md px-2 py-2 cursor-pointer transition-colors ${
              s.session_id === sessionId ? "bg-blue-600/10 border border-blue-600/40" : "hover:bg-muted border border-transparent"
            }`}
            onClick={() => onSelect(s.session_id)} data-testid={`session-item-${s.session_id}`}>
            <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{s.title || "New conversation"}</p>
              <p className="text-[10px] text-muted-foreground">{s.count} messages</p>
            </div>
            <button
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600 transition-opacity"
              onClick={(e) => { e.stopPropagation(); setToDelete(s); }}
              data-testid={`session-delete-${s.session_id}`} aria-label="Delete conversation">
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" and all its messages will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="session-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="session-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
